import {ApolloClient, gql} from "@apollo/client";
import {MySubmissionsFragment} from "../graphql/fragments";
import * as _ from 'lodash';
import {action, computed, makeObservable, observable, runInAction} from "mobx";

import {API, graphqlOperation} from "@aws-amplify/api";
import {submit} from "../graphql/mutations";
import {ContestSubmissions, SubmissionStore, SubmitState} from "./SubmissionStore";
import {MobxSubmission} from "./MobxSubmission";
import {Category, Contest, Submission, WorkType} from "./Submission";
import {MobxSubmissionItem} from "./MobxSubmissionItem";

import {ContestStore} from "./ContestStore";
import {option} from "ts-option/index";
import {CreateSubmissionMutation, ListContestOrdersQuery, MySubmissionsFragmentFragment} from "../API";
import {GraphQLResult} from "@aws-amplify/api-graphql";
import {listContestOrders} from "../graphql/queries";

const listMyContestSubmissions = gql`
  ${MySubmissionsFragment}
  query ListSubmissions($filter: ModelSubmissionFilterInput,$limit:Int, $nextToken: String) {
    listSubmissions(filter: $filter,limit:$limit, nextToken:$nextToken) {
       nextToken
       items {
          ...MySubmissionsFragment
       }
    }
  }`

export class MobxSubmissionStore implements SubmissionStore {
  submissions: Map<string, Submission> = new Map()
  user: string
  userName: string
  client: ApolloClient<any> = null
  contestStore: ContestStore
  contests: Map<string, Contest> = new Map()
  categories: Map<string, Category> = new Map()
  workTypes: Map<string, WorkType> = new Map()

  constructor(client: ApolloClient<any>, user: string, userName: string, contestStore: ContestStore) {
    this.client = client
    this.user = user
    this.userName = userName
    this.contestStore = contestStore

    makeObservable(this, {
      submissionsByYear: computed,
      user: observable,
      contests: observable,
      categories: observable,
      workTypes: observable,
      setSubmissions: action,
      submissions: observable
    })

    this.getContestSubmissionsByUser(userName)
      .then(s => runInAction(() => {
        const items = s.data.listSubmissions.items;

        if (s.data.listSubmissions.nextToken) {

          this.getContestSubmissionsByUser2(userName, s.data.listSubmissions.nextToken).then(s2 => {
            const items2 = s2.data.listSubmissions.items;

                    this
              .setSubmissions(
                new Map(
                  toMobxSubmissionItems(items.concat(items2), client, user, userName)
                    .map(submission => [submission.submissionId, submission])
                )
              )
          })

        } else {


          this
            .setSubmissions(
              new Map(
                toMobxSubmissionItems(items, client, user, userName)
                  .map(submission => [submission.submissionId, submission])
              )
            )
        }
      }))

  }

  setSubmissions(submissions: Map<string, MobxSubmission>) {
    this.submissions = submissions
  }

  getContestSubmissionsByUser(userName: string) {
    return this.client.query({


      query: listMyContestSubmissions,
      variables: {
        limit:10000,
        filter: {owner: {eq: userName}},
      }
    });
  }

  hasPaid(contest: String): Promise<boolean>  {
    return this.getPayments(contest).then(payments => payments.data.listContestOrders.items.find(order => order.paid == 1 && contest === order.contestId) != undefined)
  }

  private getPayments(contest: String) {
    return API.graphql(graphqlOperation(listContestOrders, {
        input: {
          contestId: contest,
          user: this.userName
        }
      })
    ) as Promise<GraphQLResult<ListContestOrdersQuery>>
  }

  getContestSubmissionsByUser2(userName: string, nextToken: string) {
    return this.client.query({


      query: listMyContestSubmissions,
      variables: {
        nextToken: nextToken,
        limit:10000,
        filter: {owner: {eq: userName}},
      }
    });
  }

  createNew(contestId: string, categoryId: string, workTypeId: string): Submission {
    let workType =
      option(this.contestStore.getContest(contestId).categories.find(x => x.id === categoryId)).flatMap(category => option(category.workTypes.find(x => x.id === workTypeId))).getOrElse(null)

    return new MobxSubmission(this.client, this.user, this.userName, this.contestStore.getContest(contestId), this.contestStore.getContest(contestId).categories.find(x => x.id === categoryId), workType, [])
  }

  submit(submission: Submission): Promise<SubmitState> {
    return this.doSubmit(submission).then(() => {
        submission.setSubmitted()

        this.submissions.set(submission.submissionId, submission)

        return "Submitted"
      })
      .catch((e) => {

        if (e?.errors && e.errors && e.errors[0].message === "Payment required")
          return "NotPaid"
        else {
          return "Error"

        }
      })
  }

  private doSubmit(submission: Submission) {
    return API
      .graphql(graphqlOperation(submit, {
          input: {
            contestId: submission.contest.id,
            submissionId: submission.submissionId,
            user: this.userName
          }
        })
      ) as Promise<GraphQLResult<CreateSubmissionMutation>>
  }

  submission(id: string): Submission {
    return this.submissions.get(id)
  }

  save(submission: Submission): Promise<any> {
    return submission
      .save()
      .then(id => this.submissions.set(id, submission))
  }

  get submissionsByYear(): ContestSubmissions[] {
    return _
      .entries(_.groupBy(Array.from(this.submissions.values()), s => `${s.contest.year}/${s.contest.id}`))
      .map(([yearAndId, submissions]) =>
        ({
          id: yearAndId.split('/')[1],
          year: yearAndId.split('/')[0],
          submissions: _.sortBy(submissions, x => x.createdAt).reverse()
        }))
  }
}

/**
 * Convert the raw GraphQl response to {@link MobxSubmissionItem}
 *
 * @param items
 * @param client
 * @param user
 */
function toMobxSubmissionItems(items: Array<MySubmissionsFragmentFragment>, client: ApolloClient<any>, user: string, userName: string) {
  return items.map(submission => {
    return new MobxSubmission(
      client,
      user,
      userName,
      {id: submission.contestId, year: submission.contest.year},
      submission.category,
      {
        id: submission.workTypeId,
        content: submission.workType.content,
        maxLengthMinutes: submission.workType.maxLengthMinutes,
        maxCount: submission.workType.maxCount
      },
      _.sortBy(submission
        .submissionItems
        .items
        .map(item => new MobxSubmissionItem(item.id, item.fileName, item.url, item.contentType, item.id, item.sortKey, JSON.parse(item.metadata))), item => item.sortKey),
      submission.id,
      submission.submitted.items.length > 0,
      submission.createdAt,
      submission.updatedAt
    )
  })
}

